import React, { useState } from 'react';
import { nanoid } from '@reduxjs/toolkit';
import {
  useActivatePlacementMutation,
  useBulkCreateAdsureTestsMutation,
  useGenerateAdsureTestsMutation,
  useGenerateWebTestCasesMutation,
  useGetActiveTestMutation,
  useSetupCreativeAdsureMutation,
  useSetupCreativeSelfServeMutation,
  useUploadAssetMutation,
} from 'services/api/creativeServer';
import {
  AssetDimensions,
  Option,
  StreamMetadataDetails,
} from 'containers/PodHomePage/components/Screen/components/OnDemandModal/types';
import useQuery from 'reusable/customHooks/useQuery';
import {
  describeError,
  startSelectiveSessionReplay,
  stopSelectiveSessionReplay,
  track,
} from 'analytics/posthog';

interface UseSaveWorkflowProps {
  os: string;
  country: string;
  primaryAsset: File | null;
  secondaryAsset: File | null;
  destinationAsset: File | null;
  selectedAdType: Option | null;
  selectedDestinationType: Option | null;
  selectedAppId: Option | null;
  contentTargetValue: string;
  destinationURL: string;
  selectedAdslot: Option | null;
  selectedAdslots: Option[];
  selectedContentPartner: Option[];
  campaignID: string;
  creativeID: string;
  setOnDemandModal: (value: boolean) => void;
  activeTab: 'manual' | 'mosaic' | 'webTester';
  batchItems?: AdsureBatchItem[];
  clickableStreamMetadata: StreamMetadataDetails | null;
  optionalStreamMetadata: StreamMetadataDetails | null;
  destinationStreamMetadata: StreamMetadataDetails | null;
}

export interface AdsureBatchItem {
  clientId: string;
  label: string;
  primaryAsset: File;
  primaryPreview?: string | null;
  primaryDimensions: AssetDimensions | null;
  primaryAssetError?: string;
  secondaryAsset: File | null;
  secondaryPreview?: string | null;
  secondaryDimensions: AssetDimensions | null;
  destinationAsset: File | null;
  destinationPreview?: string | null;
  destinationDimensions: AssetDimensions | null;
  selectedAdType: Option | null;
  selectedDestinationType: Option | null;
  selectedAppId: Option | null;
  contentTargetValue: string;
  destinationURL: string;
  selectedAdslots: Option[];
  selectedContentPartner: Option[];
  country: Option | null;
  osv: Option[];
  isValid?: boolean;
  validationMessage?: string;
  isVideoAutoFilled?: boolean;
  adslotValidateCheck?: any;
  adslotValidationStatus?: boolean;
  adslotValidateError?: string;
  adslotMandatoryFields?: string;
  clickableStreamUrl?: string;
  clickableStreamError?: string;
  optionalStreamUrl?: string;
  optionalStreamError?: string;
  destinationStreamUrl?: string;
  destinationStreamError?: string;
  clickableStreamMetadata: StreamMetadataDetails | null;
  optionalStreamMetadata: StreamMetadataDetails | null;
  destinationStreamMetadata: StreamMetadataDetails | null;
}

export const MAX_BATCH_TESTS = 20;

export const getMaxBatchTestsMessage = (generatedTestCount: number) =>
  `You are exceeding the max tests per batch (${generatedTestCount} > ${MAX_BATCH_TESTS})`;

export const getAdsureGeneratedTestCount = (
  items: Pick<AdsureBatchItem, 'selectedAdslots' | 'osv'>[],
) =>
  items.reduce(
    (count, item) =>
      count +
      Math.max(item.selectedAdslots.length * Math.max(item.osv.length, 1), 1),
    0,
  );

export interface UseSaveWorkflowReturn {
  progressview: boolean;
  progressStatus: React.ReactNode;
  handleSave: () => Promise<void>;
}

export const useSaveWorkflow = (
  props: UseSaveWorkflowProps,
): UseSaveWorkflowReturn => {
  const replaySource = 'adsure-ondemand-setup';
  const {
    os,
    country,
    primaryAsset,
    secondaryAsset,
    destinationAsset,
    selectedAdType,
    selectedDestinationType,
    selectedAppId,
    contentTargetValue,
    destinationURL,
    selectedAdslot,
    selectedAdslots,
    selectedContentPartner,
    campaignID,
    creativeID,
    setOnDemandModal,
    activeTab,
    batchItems = [],
    clickableStreamMetadata,
    optionalStreamMetadata,
    destinationStreamMetadata,
  } = props;
  const [query, setQuery] = useQuery();

  const [progressview, setProgressview] = useState(false);
  const [progressStatus, setProgressStatus] = useState<React.ReactNode>(
    'Cooking up some ad magic ...',
  );
  const [uploadAsset] = useUploadAssetMutation();
  const [setupCreative] = useSetupCreativeAdsureMutation();
  const [generateTestCases] = useGenerateAdsureTestsMutation();
  const [bulkCreateAdsureTests] = useBulkCreateAdsureTestsMutation();
  const [generateWebTesterTestCases] = useGenerateWebTestCasesMutation();
  const [getActiveTest] = useGetActiveTestMutation();
  const adsureOnDemandContext = {
    country,
    adslot: selectedAdslot?.value,
    ad_type: selectedAdType?.value,
    destination_type: selectedDestinationType?.value,
    ondemand_tab: campaignID || creativeID ? 'mosaic' : 'manual',
    has_secondary_asset: !!secondaryAsset,
    has_destination_asset: !!destinationAsset,
    content_partner_count: selectedContentPartner?.length || 0,
    osv: os,
    surface: 'adsure',
  };

  const osOptionToTarget = (value: string) => ({
    osVersion: value.replace(/^webOS|^wee/, ''),
    make: value.includes('webOS') ? 'LG' : 'wee',
  });

  const getOsValues = () =>
    os
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

  const optionFromValue = (value: string): Option => ({
    key: value,
    value,
    label: value,
  });

  const getBatchItemReadinessError = (item: AdsureBatchItem) => {
    if (!item.isValid) {
      return item.validationMessage || 'Please correct invalid batch tests.';
    }
    if (item.primaryAssetError) return item.primaryAssetError;
    if (!item.primaryAsset) return 'Please upload the primary asset.';
    if (!item.selectedAdType) return 'Please select Ad Type.';
    if (!item.selectedDestinationType) return 'Please select Destination Type.';
    if (!item.country) return 'Please select Country.';
    if (!item.osv || item.osv.length === 0) return 'Please select OS Versions.';
    if (!item.selectedAdslots || item.selectedAdslots.length === 0) {
      return 'Please select at least one Ad Slot.';
    }
    return '';
  };

  const assertBatchItemsReadyForSave = (items: AdsureBatchItem[]) => {
    const invalidItem = items.find(item => getBatchItemReadinessError(item));
    if (!invalidItem) return;

    throw new Error(
      `${invalidItem.label}: ${getBatchItemReadinessError(invalidItem)}`,
    );
  };

  const getCurrentManualItem = (): AdsureBatchItem | null => {
    if (
      !primaryAsset ||
      !selectedAdType ||
      !selectedDestinationType ||
      !country ||
      selectedAdslots.length === 0 ||
      getOsValues().length === 0
    ) {
      return null;
    }

    return {
      clientId: 'current',
      label: primaryAsset.name,
      primaryAsset,
      primaryDimensions: null,
      secondaryAsset,
      secondaryDimensions: null,
      destinationAsset,
      destinationDimensions: null,
      selectedAdType,
      selectedDestinationType,
      selectedAppId,
      contentTargetValue,
      destinationURL,
      selectedAdslots,
      selectedContentPartner,
      country: optionFromValue(country),
      osv: getOsValues().map(optionFromValue),
      clickableStreamMetadata,
      optionalStreamMetadata,
      destinationStreamMetadata,
    };
  };

  const buildDestinationDetails = (
    destinationType: string,
    selectedApp: Option | null,
    contentTarget: string,
    url: string,
    destinationAssetDetails: any,
    streamMetadata?: StreamMetadataDetails | null,
  ) =>
    destinationType === 'App'
      ? { appGroupId: selectedApp?.value }
      : destinationType === 'Deeplink'
        ? {
            appGroupId: selectedApp?.value,
            contentTarget,
          }
        : destinationType === 'Web' || destinationType === 'WebFS'
          ? { destURL: url }
          : destinationType === 'Image' || destinationType === 'Video'
            ? {
                destURL:
                  destinationAssetDetails?.mosaicResponse?.insertedValues?.[0]
                    ?.url,
              }
            : destinationType === 'Stream'
              ? {
                  destURL: streamMetadata?.url || url.trim(),
                  duration: streamMetadata?.defaultDuration,
                  mimeType: streamMetadata?.mimeType,
                  width: streamMetadata?.width,
                  height: streamMetadata?.height,
                  streamType: streamMetadata?.streamType,
                }
              : {};

  const uploadAssetsForCase = async ({
    uploadTestId,
    primary,
    secondary,
    destination,
    adType,
    progressPrefix,
  }: {
    uploadTestId: string;
    primary: File;
    secondary: File | null;
    destination: File | null;
    adType: string;
    progressPrefix: string;
  }) => {
    const assets: Record<string, any> = {};
    const progressLabel = progressPrefix ? `${progressPrefix} ` : '';
    setProgressStatus(`${progressLabel}Uploading Primary Asset ...`);
    const primaryAssetDetails: any = await uploadAsset({
      testId: uploadTestId,
      type: 'adsure',
      asset: primary,
      assetType: 'primaryAsset',
    }).unwrap();
    assets.primaryAsset =
      primaryAssetDetails?.mosaicResponse?.insertedValues?.[0];

    let secondaryAssetDetails: any = null;
    if (secondary) {
      setProgressStatus(`${progressLabel}Uploading Secondary Asset ...`);
      const assetName =
        adType === 'clickableVideoTo'
          ? 'clickableVideoAsset'
          : 'secondaryAsset';
      secondaryAssetDetails = await uploadAsset({
        testId: uploadTestId,
        type: 'adsure',
        asset: secondary,
        assetType: assetName,
      }).unwrap();
      assets[assetName] =
        secondaryAssetDetails?.mosaicResponse?.insertedValues?.[0];
    }

    let destinationAssetDetails: any = null;
    if (destination) {
      setProgressStatus(`${progressLabel}Uploading Destination Asset ...`);
      destinationAssetDetails = await uploadAsset({
        testId: uploadTestId,
        type: 'adsure',
        asset: destination,
        assetType: 'destinationAsset',
      }).unwrap();
      assets.destinationAsset =
        destinationAssetDetails?.mosaicResponse?.insertedValues?.[0];
    }

    return { assets, destinationAssetDetails };
  };

  const handleSave = async () => {
    const skip = activeTab === 'webTester';
    console.log('skip: ', skip);
    setProgressview(true);
    startSelectiveSessionReplay(replaySource);
    track('ondemand save clicked', adsureOnDemandContext);
    // check for any existing sessions
    const newTestId = nanoid();
    try {
      let setupPayload: any = {};
      let mainAdSlotForGeneration = selectedAdslot?.value || '';
      const isManualAssetUpload =
        activeTab === 'manual' &&
        campaignID.trim() === '' &&
        creativeID.trim() === '' &&
        !skip;
      const currentManualItem = getCurrentManualItem();
      const manualItems =
        batchItems.length > 0
          ? batchItems
          : currentManualItem
            ? [currentManualItem]
            : [];
      if (isManualAssetUpload && batchItems.length > 0) {
        assertBatchItemsReadyForSave(batchItems);
      }
      const generatedTestCount = getAdsureGeneratedTestCount(manualItems);
      if (
        isManualAssetUpload &&
        generatedTestCount > MAX_BATCH_TESTS
      ) {
        throw new Error(getMaxBatchTestsMessage(generatedTestCount));
      }
      const shouldUseBulkAdsureCreate =
        isManualAssetUpload &&
        manualItems.length > 0 &&
        (batchItems.length > 0 || generatedTestCount > 1);

      if (shouldUseBulkAdsureCreate) {
        const testCases: any[] = [];
        let caseIndex = 0;

        for (
          let itemIndex = 0;
          itemIndex < manualItems.length;
          itemIndex += 1
        ) {
          const item = manualItems[itemIndex];
          const uploadTestId = `${newTestId}_${item.clientId}`;
          const { assets, destinationAssetDetails } = await uploadAssetsForCase(
            {
              uploadTestId,
              primary: item.primaryAsset,
              secondary: item.secondaryAsset,
              destination: item.destinationAsset,
              adType: item.selectedAdType!.value,
              progressPrefix: `(${itemIndex + 1}/${manualItems.length})`,
            },
          );

          for (const adslot of item.selectedAdslots) {
            caseIndex += 1;
            const caseId = `${newTestId}-${item.clientId}-${caseIndex}`;
            testCases.push({
              caseId,
              assetLabel: item.label,
              adslot: adslot.value || adslot.label,
              adtype: item.selectedAdType!.value,
              destinationType: item.selectedDestinationType!.value,
              destinationDetails: buildDestinationDetails(
                item.selectedDestinationType!.value,
                item.selectedAppId,
                item.contentTargetValue,
                item.destinationURL,
                destinationAssetDetails,
                item.destinationStreamMetadata,
              ),
              country: item.country!.label,
              osv: item.osv.map(option => option.value),
              contentPartners:
                adslot.label === 'portal/screensaver@main'
                  ? item.selectedContentPartner?.map(cp => cp.value)
                  : null,
              region: 'na',
              assets,
              ...(item.selectedAdType!.value === 'clickableStreamTo' &&
              item.clickableStreamMetadata
                ? {
                    clickableStreamDetails: item.clickableStreamMetadata,
                  }
                : {}),
              ...(item.selectedAdType!.value === 'expandableBannerTo' &&
              item.optionalStreamMetadata
                ? {
                    optionalStreamDetails: item.optionalStreamMetadata,
                  }
                : {}),
            });
          }
        }

        setProgressStatus(
          `(4/6) Uploaded ${manualItems.length} creative asset set${
            manualItems.length === 1 ? '' : 's'
          } and prepared ${generatedTestCount} test combination${
            generatedTestCount === 1 ? '' : 's'
          } ...`,
        );

        setupPayload = {
          testId: newTestId,
          country: manualItems[0].country!.label,
          osv: manualItems[0].osv.map(option => option.value),
          testCases,
        };
      } else if (isManualAssetUpload && batchItems.length === 1) {
        const item = batchItems[0];
        const adslot = item.selectedAdslots[0];
        mainAdSlotForGeneration = adslot?.value || adslot?.label || '';
        const { assets, destinationAssetDetails } = await uploadAssetsForCase({
          uploadTestId: newTestId,
          primary: item.primaryAsset,
          secondary: item.secondaryAsset,
          destination: item.destinationAsset,
          adType: item.selectedAdType!.value,
          progressPrefix: '',
        });

        setProgressStatus('(4/6) All Assets Uploaded Successfully ...');

        setupPayload = {
          adslot: adslot?.value || adslot?.label,
          adtype: item.selectedAdType!.value,
          destinationType: item.selectedDestinationType!.value,
          destinationDetails: buildDestinationDetails(
            item.selectedDestinationType!.value,
            item.selectedAppId,
            item.contentTargetValue,
            item.destinationURL,
            destinationAssetDetails,
            item.destinationStreamMetadata,
          ),
          country: item.country!.label,
          osv: item.osv.map(option => option.value),
          contentPartners:
            adslot?.label === 'portal/screensaver@main'
              ? item.selectedContentPartner?.map(cp => cp.value)
              : null,
          region: 'na', // backend will correct the value based on the country
          testId: newTestId,
          assets,
          ...(item.selectedAdType!.value === 'clickableStreamTo' &&
          item.clickableStreamMetadata
            ? {
                clickableStreamDetails: item.clickableStreamMetadata,
              }
            : {}),
          ...(item.selectedAdType!.value === 'expandableBannerTo' &&
          item.optionalStreamMetadata
            ? {
                optionalStreamDetails: item.optionalStreamMetadata,
              }
            : {}),
        };
      } else if (isManualAssetUpload) {
        const assets = {};
        console.log('TestId', newTestId);
        setProgressStatus('(1/6) Uploading Primary Asset ...');

        // Upload primary asset
        const primaryAssetDetails: any = await uploadAsset({
          testId: newTestId,
          type: 'adsure',
          asset: primaryAsset!,
          assetType: 'primaryAsset',
        }).unwrap();

        // Handle success
        console.log('Upload successful:', primaryAssetDetails);
        assets['primaryAsset'] =
          primaryAssetDetails?.mosaicResponse?.insertedValues?.[0];
        let secondaryAssetDetails: any = null;
        if (secondaryAsset) {
          setProgressStatus('(2/6) Uploading Secondary Asset ...');
          // Upload secondary asset if exists
          const assetName =
            selectedAdType?.value === 'clickableVideoTo'
              ? 'clickableVideoAsset'
              : 'secondaryAsset';
          secondaryAssetDetails = await uploadAsset({
            testId: newTestId,
            type: 'adsure',
            asset: secondaryAsset,
            assetType: assetName,
          }).unwrap();

          // Handle success
          console.log('Secondary Upload successful:', secondaryAssetDetails);
          assets[assetName] =
            secondaryAssetDetails?.mosaicResponse?.insertedValues?.[0];
        }
        let destinationAssetDetails: any = null;
        if (destinationAsset) {
          // Upload destination asset if exists
          setProgressStatus('(3/6) Uploading Destination Asset ...');
          destinationAssetDetails = await uploadAsset({
            testId: newTestId,
            type: 'adsure',
            asset: destinationAsset,
            assetType: 'destinationAsset',
          }).unwrap();

          // Handle success
          console.log(
            'Destination Upload successful:',
            destinationAssetDetails,
          );
          assets['destinationAsset'] =
            destinationAssetDetails?.mosaicResponse?.insertedValues?.[0];
        }

        setProgressStatus('(4/6) All Assets Uploaded Successfully ...');

        setupPayload = {
          adslot: selectedAdslot?.value,
          adtype: selectedAdType?.value,
          destinationType: selectedDestinationType?.value,
          destinationDetails: buildDestinationDetails(
            selectedDestinationType?.value || '',
            selectedAppId,
            contentTargetValue,
            destinationURL,
            destinationAssetDetails,
            destinationStreamMetadata,
          ),
          country,
          osv: getOsValues(),
          contentPartners:
            selectedAdslot?.label === 'portal/screensaver@main'
              ? selectedContentPartner?.map(cp => cp.value)
              : null,
          region: 'na', // backend will correct the value based on the country
          testId: newTestId,
          assets,
          ...(selectedAdType?.value === 'clickableStreamTo' &&
          clickableStreamMetadata
            ? {
                clickableStreamDetails: clickableStreamMetadata,
              }
            : {}),
          ...(selectedAdType?.value === 'expandableBannerTo' &&
          optionalStreamMetadata
            ? {
                optionalStreamDetails: optionalStreamMetadata,
              }
            : {}),
        };
      } else if (skip) {
        setupPayload = {
          destinationType: selectedDestinationType?.value,
          osv: os
            .split(',')
            .map(s => s.trim())
            .map(el => ({
              osVersion: el.replace(/^webOS|^wee/, ''),
              make: el.includes('webOS') ? 'LG' : 'wee',
            })),
          testId: newTestId,
          country: country,
          destinationURL: destinationURL,
          activeTab,
        };
      } else {
        setupPayload = {
          osv: os.split(',').map(s => s.trim()),
          testId: newTestId,
          cid: Number(campaignID),
          crid: Number(creativeID),
        };
      }
      setProgressStatus(
        shouldUseBulkAdsureCreate
          ? '(5/6) Creating Adsure Batch Tests ...'
          : '(5/6) Updating Creative ...',
      );
      let setupResult: any = true;
      let activateResult: any = null;
      if (!skip) {
        setupResult = shouldUseBulkAdsureCreate
          ? await bulkCreateAdsureTests(setupPayload).unwrap()
          : await setupCreative(setupPayload).unwrap();
      }
      console.log(setupResult);
      // Generate Test Cases
      setProgressStatus('(6/6) Generating Adsure Test Cases ...');

      if (skip) {
        activateResult = await generateWebTesterTestCases({
          metadata: setupPayload,
        }).unwrap();
      } else if (shouldUseBulkAdsureCreate) {
        activateResult = setupResult;
      } else {
        activateResult = await generateTestCases({
          osv: setupResult.testMetadata.osv.map(osOptionToTarget),
          creative: setupResult.creative,
          campaign: setupResult.campaign,
          userFlow: setupResult.userFlow,
          mainAdSlot: mainAdSlotForGeneration,
          user: setupResult.user,
          testId: setupResult.testMetadata.testId,
          tvServerEnvironment: setupResult.testMetadata.tvServerEnvironment,
        }).unwrap();
      }
      console.log(activateResult);
      !skip
        ? track('adsure ondemand test generated', {
            ...adsureOnDemandContext,
            test_id: setupResult.testMetadata.testId,
            cid: setupResult?.campaign?.cid,
            crid: setupResult?.creative?.id,
            batch_case_count: setupResult?.cases?.length || 1,
            osv_count: setupResult?.batch
              ? setupResult.cases.reduce(
                  (count, testCase) => count + testCase.osv.length,
                  0,
                )
              : setupResult.testMetadata.osv.length,
          })
        : track('webtester ondemand test generated', {
            ...adsureOnDemandContext,
            test_id: newTestId,
            osv_count: setupPayload.osv.length,
          });
      const d = new Date();
      d.setDate(d.getDate() + 1); // add 1 day
      const tomorrow = d.toISOString().split('T')[0];
      !skip
        ? setQuery({
            testTag: 'on-demand',
            alphCid: setupResult?.campaign?.cid || '',
            alphCrid: setupResult?.creative?.id || '',
            testId: setupResult?.batch ? newTestId : '',
            page: '1',
            endDate: tomorrow,
            status: '',
            myCampaigns: 'false',
          })
        : setQuery({
            testTag: 'on-demand',
            testId: newTestId,
            page: '1',
            endDate: tomorrow,
            myCampaigns: 'false',
            status: '',
          });
      setProgressStatus(
        '🚀 All Setup Complete! Test Cases have been generated!',
      );
      await new Promise(resolve => setTimeout(resolve, 3000));
      setProgressview(false);
      setOnDemandModal(false);
      stopSelectiveSessionReplay(replaySource);
    } catch (error: any) {
      track('ondemand setup failed', {
        ...adsureOnDemandContext,
        error_message: describeError(error),
      });
      console.error('Handle Save Error: ', error);

      setProgressStatus(
        <>
          Setup failed. Please try again.
          <br />
          Error: {error?.message || 'Unknown error'}
          <br />
          <span className="font-bold">
            If this happens again, contact the TV Lab team on Slack:&nbsp;{' '}
          </span>
          <a
            href="https://alphonso.slack.com/archives/C02F8QW3FRA"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#4A9EFF', textDecoration: 'underline' }}
          >
            #lg-tv-remote-testing
          </a>
        </>,
      );

      await new Promise(resolve => setTimeout(resolve, 5000));
      setProgressview(false);
      stopSelectiveSessionReplay(replaySource);
    }
  };

  return {
    progressview,
    progressStatus,
    handleSave,
  };
};
